import React from 'react';
import { Link } from 'react-router-dom';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import SponsorsSection from '../components/SponsorsSection';
import ScrollToTop from '../components/ScrollToTop';
import TornPaperWrapper from '../components/TornPaperWrapper';
import { getArabicTranslation } from '../utils/arabicTranslations';
import { useCms } from '../cms/CmsContext';
import { buildLineupDaySections, getArtistLineup } from '../data/artistLineup';
import './MusicEntertainment.css';

const PerformerGrid = ({ performers, ticketPath, compact }) => (
  <div className={`music-ent-grid ${compact ? 'music-ent-grid-2' : ''}`}>
    {performers.map((performer, index) => (
      <Link
        key={`${performer.name}-${index}`}
        to={ticketPath}
        className="music-ent-card-link"
        title={performer.name}
      >
        <img
          loading="lazy"
          src={performer.image}
          alt={performer.name}
          className="music-ent-card-image"
        />
      </Link>
    ))}
  </div>
);

const StageSection = ({ name, ticketPath, buttonText, performers, hasPacAmp }) => (
  <div className="music-ent-stage-block">
    <h3 className="music-ent-stage-title">
      {name}
      <span className="arabic-text on-white">{getArabicTranslation(name)}</span>
    </h3>
    <p className="music-ent-stage-note">
      {hasPacAmp ? 'Festival or PAC AMP ticket required.' : 'Festival ticket required.'} Purchase festival tickets{' '}
      <Link to={ticketPath} className="music-ent-inline-link" title={name}>
        here
      </Link>{' '}
      (<span className="music-ent-price-amount">starting at $20</span>)
    </p>
    <PerformerGrid performers={performers} ticketPath={ticketPath} compact={performers.length === 2} />
    <div className="music-ent-stage-btn-wrap">
      <Link to={ticketPath} className="music-ent-stage-btn">
        {buttonText}
      </Link>
    </div>
  </div>
);

const MusicEntertainment = () => {
  const { content } = useCms();
  const daySections = buildLineupDaySections(getArtistLineup(content));

  return (
    <div className="page-wrapper">
      <AnnouncementBar />

      <section className="hero-section">
        <div className="hero-background-wrapper">
          <video
            src={"/hero-final.mp4"}
            alt="oc mena festival"
            className="hero-background-video"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="hero-gradient-overlay"></div>
        </div>

        <TornPaperWrapper>
          <section className="music-ent-container">
            <h1 className="music-ent-heading">
              Music &amp; Entertainment
              <span className="arabic-text on-white">{getArabicTranslation('Music & Entertainment')}</span>
            </h1>
            <p className="music-ent-description">
              Experience an unforgettable lineup of artists and performers at the OC MENA Festival. Schedule and timing may change at any time.
              Please bookmark this page for reference and stay tuned for more announcements. Follow our Instagram for up-to-date news.
            </p>

            {daySections.map((day) => (
              <article key={day.key} className="music-ent-day-section">
                <header className="music-ent-day-header">
                  <h2>
                    {day.dayLabel}
                    <span className={`arabic-text on-dark arabic-inline ${day.key === 'sunday' ? 'sunday-arabic' : ''}`}>{getArabicTranslation(day.dayLabel)}</span>
                  </h2>
                  <p>{day.dateLabel}</p>
                </header>

                <div className="music-ent-stage-block music-ent-pac-block">
                  {day.pacAmp.hasPerformances && (
                    <h3 className="music-ent-stage-title">
                      Pacific Amphitheatre (PAC AMP Stage)
                      <span className="arabic-text on-white">{getArabicTranslation('Pacific Amphitheatre')}</span>
                    </h3>
                  )}
                  {day.pacAmp.hasPerformances ? (
                    <>
                      <p className="music-ent-stage-note">
                        {day.pacAmp.notePrefix} Purchase tickets{' '}
                        <Link to={day.pacAmp.ticketPath} className="music-ent-inline-link" title={day.pacAmp.djName}>
                          here
                        </Link>{' '}
                        (<span className="music-ent-price-amount">starting at $32</span>)
                      </p>
                      <PerformerGrid performers={day.pacAmp.performers} ticketPath={day.pacAmp.ticketPath} />
                      {day.pacAmp.djName && <div className="music-ent-dj-row">
                        <span className="music-ent-dj-label">DJ Appearance by</span>
                        {day.pacAmp.djImage && <img
                          src={day.pacAmp.djImage}
                          alt={`DJ ${day.pacAmp.djName}`}
                          className="music-ent-dj-avatar"
                        />}
                        <span className="music-ent-dj-name">{day.pacAmp.djName}</span>
                      </div>}
                      <div className="music-ent-stage-btn-wrap">
                        <Link to={day.pacAmp.ticketPath} className="music-ent-stage-btn">
                          {day.pacAmp.buttonText}
                        </Link>
                      </div>
                    </>
                  ) : (
                    <div className="music-ent-no-pac">{day.pacAmp.noPerformanceText}</div>
                  )}
                </div>

                {day.stages.map((stage) => (
                  <StageSection
                    key={`${day.key}-${stage.name}`}
                    name={stage.name}
                    ticketPath={stage.ticketPath}
                    buttonText={stage.buttonText}
                    performers={stage.performers}
                    hasPacAmp={day.pacAmp.hasPerformances}
                  />
                ))}
              </article>
            ))}

            <div className="music-ent-support-images">
              <div className="music-ent-support-image-frame">
                <img
                  loading="lazy"
                  src="/ocmena-official-flyer(1080x1350)-c.png"
                  alt="OC MENA Festival Official Flyer"
                  className="music-ent-support-image"
                />
              </div>
              <div className="music-ent-support-image-frame">
                <img
                  loading="lazy"
                  src="/how_tickets_work.jpeg"
                  alt="How Tickets Work"
                  className="music-ent-support-image"
                />
              </div>
              <div className="music-ent-support-image-frame">
                <img
                  loading="lazy"
                  src="/map.png"
                  alt="OC MENA Festival Event Map"
                  className="music-ent-support-image"
                />
              </div>
            </div>
          </section>
        </TornPaperWrapper>
      </section>

      <SponsorsSection />
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default MusicEntertainment;
